import { CaretDownIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SearchableSelectProps {
  label?: string;
  name?: string;
  options: SearchableSelectOption[];
  className?: string;
  onChange?: (value: string) => void;
  value?: string;
  defaultValue?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  noOptionsText?: string;
  onSearch?: (query: string) => Promise<SearchableSelectOption[]>;
  searchingText?: string;
  valueLabel?: string; // optional label for current value when options are empty (remote mode)
  searchId?: string; // unique identifier to prevent state interference
}

/**
 * Lightweight searchable single-select dropdown.
 * - Client-side filtering by option label
 * - Keyboard navigation (ArrowUp/Down, Enter, Escape)
 * - Controlled via `value` prop, also supports `defaultValue`
 */
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  options,
  className,
  onChange,
  value,
  defaultValue,
  error,
  placeholder = 'Search or select...',
  disabled = false,
  noOptionsText = 'No results',
  onSearch,
  searchingText = 'Searching...',
  valueLabel,
  searchId: _searchId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Create a unique identifier for this instance to prevent state interference
  // const _instanceId = useRef(
  //   searchId || `searchable-select-${Math.random().toString(36).substr(2, 9)}`
  // );

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [selectedOption, setSelectedOption] =
    useState<SearchableSelectOption | null>(null);

  const [serverOptions, setServerOptions] = useState<
    SearchableSelectOption[] | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);

  const displayedOptions = useMemo(() => {
    if (onSearch) {
      return serverOptions || [];
    }
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [onSearch, serverOptions, options, query]);

  // Keep selected option in sync with controlled value/defaultValue/option changes
  useEffect(() => {
    if (onSearch) {
      if (!value) {
        setSelectedOption(null);
        return;
      }
      const serverMatch = serverOptions?.find(option => option.value === value);
      if (serverMatch) {
        setSelectedOption(serverMatch);
        return;
      }
      if (valueLabel) {
        setSelectedOption({ value, label: valueLabel });
        return;
      }
      return;
    }
    const newSelected = options.find(option => option.value === value);
    setSelectedOption(newSelected || null);
  }, [value, defaultValue, options, onSearch, serverOptions, valueLabel]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setQuery('');
      setHighlightIndex(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleSelect = (option: SearchableSelectOption) => {
    if (option.disabled) return;
    setSelectedOption(option);
    onChange?.(option.value);
    setIsOpen(false);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i =>
        Math.min(i + 1, Math.max(displayedOptions.length - 1, 0))
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = displayedOptions[highlightIndex];
      if (option) handleSelect(option);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Debounced server-side search
  useEffect(() => {
    if (!onSearch || !isOpen) return;
    let cancelled = false;
    setIsSearching(true);

    const handle = setTimeout(async () => {
      try {
        const result = await onSearch(query.trim());
        if (!cancelled) {
          setServerOptions(result);
          setHighlightIndex(0);
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, onSearch, isOpen]);

  // Reset server results when closing
  useEffect(() => {
    if (!onSearch) return;
    if (!isOpen) {
      setServerOptions(null);
      setIsSearching(false);
    }
  }, [isOpen, onSearch]);

  const currentLabel = selectedOption?.label ?? valueLabel ?? placeholder;

  return (
    <div
      ref={containerRef}
      className={`relative inline-block ${className || ''}`}
    >
      {label && (
        <label className='block text-sm font-medium text-gray-70'>
          {label}
        </label>
      )}
      <div
        onClick={toggleDropdown}
        className={`flex justify-between items-center h-[40px] gap-2 px-4 py-2 mt-2 bg-transparent border rounded-full cursor-pointer shadow-sm hover:shadow-md ${
          error ? 'border-destructive-50' : 'border-[#9D9898]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        role='button'
      >
        <section className='flex gap-2 items-center min-w-0'>
          <span className='text-sm text-[#1D2939] capitalize font-medium truncate'>
            {currentLabel}
          </span>
        </section>
        <CaretDownIcon className='w-4 h-4' />
      </div>

      {isOpen && (
        <div className='absolute left-0 mt-2 w-full bg-white border border-[#E5E7EB] rounded-md shadow-md max-h-72 overflow-hidden z-10'>
          <div className='p-2 border-b border-[#E5E7EB] flex items-center gap-2'>
            <MagnifyingGlassIcon className='w-4 h-4' />
            <input
              ref={inputRef}
              type='text'
              value={query}
              onChange={e => {
                setQuery(e.target.value);
                setHighlightIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder='Type to search'
              className='w-full outline-none text-sm'
            />
          </div>
          <ul className='max-h-56 overflow-y-auto'>
            {onSearch && isSearching && (
              <li className='px-4 py-3 text-sm text-gray-50'>
                {searchingText}
              </li>
            )}
            {!isSearching && displayedOptions.length === 0 && (
              <li className='px-4 py-3 text-sm text-gray-50'>
                {noOptionsText}
              </li>
            )}
            {displayedOptions.map((option, idx) => {
              const isHighlighted = idx === highlightIndex;
              const isSelected = selectedOption?.value === option.value;
              return (
                <li
                  key={option.value}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onClick={() => handleSelect(option)}
                  className={`flex items-center gap-2 px-4 py-2 cursor-pointer select-none ${
                    isHighlighted ? 'bg-gray-10' : ''
                  } ${option.disabled ? 'opacity-50 pointer-events-none' : ''}`}
                  role='option'
                  aria-selected={isSelected}
                >
                  {option.icon && (
                    <span className='w-4 h-4 flex items-center'>
                      {option.icon}
                    </span>
                  )}
                  <span className='text-sm text-[#1D2939]'>{option.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {error && (
        <span className='text-xs text-destructive-60 mt-1'>{error}</span>
      )}
    </div>
  );
};

export default React.memo(SearchableSelect);
